import {
  Page,
  Document,
  View,
  Text,
  StyleSheet,
  Font,
  Svg,
  Rect,
  G,
} from '@react-pdf/renderer';
import { IStatementPDF } from '../../shared/interfaces/istatementPDF.interface';
import { Colors } from '../../shared/theme/styles.enum';
import { formatCurrency, getLocaleDate } from '../../shared/utils/gridUtils';
import { getStatementPeriod } from '../../shared/utils/pdfUtils';
import {
  CUSTOMER_SERVICE_TITLE1,
  CUSTOMER_SERVICE_SUBTITLE1,
  CUSTOMER_SERVICE_TITLE2,
  CUSTOMER_SERVICE_TITLE3,
  CUSTOMER_SERVICE_SUBTITLE3_1,
  CUSTOMER_SERVICE_SUBTITLE3_3,
  DISCLOSURES_TITLE1,
  DISCLOSURES_PARAGRAPH1,
  DISCLOSURES_TITLE2,
  DISCLOSURES_PARAGRAPH2,
  CUSTOMER_SERVICE_SUBTITLE2,
  CUSTOMER_SERVICE_SUBTITLE3_2,
} from '../../shared/utils/textConstants';

interface IStatementPDFProps {
  statements: IStatementPDF[];
}

Font.register({
  family: 'Inter',
  fonts: [
    {
      src: 'https://fonts.gstatic.com/s/inter/v18/UcCO3FwrK3iLTeHuS_nVMrMxCp50SjIw2boKoduKmMEVuLyeAZFhjQ.ttf',
      fontWeight: 300,
    },
    {
      src: 'https://fonts.gstatic.com/s/inter/v18/UcCO3FwrK3iLTeHuS_nVMrMxCp50SjIw2boKoduKmMEVuLyfAZFhjQ.ttf',
      fontWeight: 'normal',
    },
    {
      src: 'https://fonts.gstatic.com/s/inter/v18/UcCO3FwrK3iLTeHuS_nVMrMxCp50SjIw2boKoduKmMEVuI6fAZFhjQ.ttf',
      fontWeight: 500,
    },
    {
      src: 'https://fonts.gstatic.com/s/inter/v18/UcCO3FwrK3iLTeHuS_nVMrMxCp50SjIw2boKoduKmMEVuGKYAZFhjQ.ttf',
      fontWeight: 600,
    },
    {
      src: 'https://fonts.gstatic.com/s/inter/v18/UcCO3FwrK3iLTeHuS_nVMrMxCp50SjIw2boKoduKmMEVuFuYAZFhjQ.ttf',
      fontWeight: 700,
    },
  ],
});

const HEADER_BG = '#555555';
const BRAND_COLOR = Colors.Teal;
const BRAND_DARK = '#2d7a6d';
const LIGHT_BG = '#F7F9F9';
const BORDER_COLOR = '#D6E0DE';
const TEXT_DARK = '#1A1A1A';
const TEXT_SECONDARY = '#4A5568';

const COL1_WIDTH = 28;
const COLN_WIDTH = 14.5;
const SUB_COL1_WIDTH = 25;
const SUB_COL2_WIDTH = 30;
const SUB_COLN_WIDTH = 15;

const styles = StyleSheet.create({
  page: {
    paddingTop: 0,
    paddingBottom: 50,
    paddingHorizontal: 0,
    fontFamily: 'Inter',
    color: TEXT_DARK,
  },
  // Top brand bar
  brandBar: {
    backgroundColor: HEADER_BG,
    padding: '20px 35px 16px 35px',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
  },
  logoContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  statementLabel: {
    fontFamily: 'Inter',
    fontSize: 13,
    fontWeight: 600,
    color: Colors.White,
    textAlign: 'right',
  },
  statementPeriodLabel: {
    fontFamily: 'Inter',
    fontSize: 9,
    color: Colors.White,
    opacity: 0.85,
    textAlign: 'right',
  },
  // Account name below header
  accountNameBar: {
    padding: '12px 35px 8px 35px',
  },
  accountName: {
    fontFamily: 'Inter',
    fontSize: 20,
    fontWeight: 600,
    color: TEXT_DARK,
  },
  accountSubdomain: {
    fontFamily: 'Inter',
    fontSize: 9,
    color: TEXT_SECONDARY,
    marginTop: 2,
  },
  // Content area
  content: {
    padding: '0 35px',
  },
  // Account info strip
  accountInfoStrip: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    backgroundColor: LIGHT_BG,
    borderBottom: `1px solid ${BORDER_COLOR}`,
    padding: '10px 35px',
    marginBottom: 20,
  },
  accountInfoItem: {
    flexDirection: 'column',
  },
  accountInfoLabel: {
    fontFamily: 'Inter',
    fontSize: 7.5,
    fontWeight: 600,
    color: TEXT_SECONDARY,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    marginBottom: 2,
  },
  accountInfoValue: {
    fontFamily: 'Inter',
    fontSize: 10,
    fontWeight: 'normal',
    color: TEXT_DARK,
  },
  // Product tables
  table: {
    width: 'auto',
    fontFamily: 'Inter',
    fontSize: 10,
    fontWeight: 'normal',
    lineHeight: 'normal',
    textAlign: 'left',
    marginBottom: 20,
    borderRadius: 2,
    border: `0.5px solid ${BORDER_COLOR}`,
  },
  tableHeaderRow: {
    flexDirection: 'row',
    backgroundColor: '#f8f9f9',
    borderBottom: `1px solid ${BORDER_COLOR}`,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottom: `0.5px solid ${BORDER_COLOR}`,
  },
  tableRowAlt: {
    flexDirection: 'row',
    backgroundColor: LIGHT_BG,
    borderBottom: `0.5px solid ${BORDER_COLOR}`,
  },
  tableCol1Header: {
    width: COL1_WIDTH + '%',
    padding: '8px 5px',
  },
  tableColHeader: {
    width: COLN_WIDTH + '%',
    padding: '8px 5px',
  },
  tableCellHeader: {
    fontSize: 7.5,
    fontWeight: 600,
    color: TEXT_SECONDARY,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  tableCol1: {
    width: COL1_WIDTH + '%',
    padding: '6px 5px',
  },
  tableCol: {
    width: COLN_WIDTH + '%',
    padding: '6px 5px',
  },
  tableCell: {
    fontSize: 9,
    color: TEXT_DARK,
  },
  tableCellBold: {
    fontSize: 9,
    fontWeight: 600,
    color: TEXT_DARK,
  },
  // Sub-tables (transactions, allocation history)
  subTableContainer: {
    marginLeft: '5%',
    marginRight: '2%',
    marginBottom: 8,
    borderLeft: `2px solid ${BRAND_COLOR}`,
  },
  subTableHeaderRow: {
    flexDirection: 'row',
    backgroundColor: LIGHT_BG,
    borderBottom: `0.5px solid ${BORDER_COLOR}`,
  },
  subTableRow: {
    flexDirection: 'row',
    borderBottom: `0.5px solid ${BORDER_COLOR}`,
  },
  tableCol1SubHeader: {
    width: SUB_COL1_WIDTH + '%',
    padding: '5px 8px',
  },
  tableCol2SubHeader: {
    width: SUB_COL2_WIDTH + '%',
    padding: '5px 5px',
  },
  tableColSubHeader: {
    width: SUB_COLN_WIDTH + '%',
    padding: '5px 5px',
  },
  subCellHeader: {
    fontSize: 7,
    fontWeight: 600,
    color: TEXT_SECONDARY,
    textTransform: 'uppercase',
    letterSpacing: 0.3,
  },
  tableColSub1: {
    width: SUB_COL1_WIDTH + '%',
    padding: '4px 8px',
  },
  tableColSub2: {
    width: SUB_COL2_WIDTH + '%',
    padding: '4px 5px',
  },
  tableColSub: {
    width: SUB_COLN_WIDTH + '%',
    padding: '4px 5px',
  },
  subCell: {
    fontSize: 8.5,
    color: TEXT_DARK,
  },
  // Empty state
  emptyTable: {
    width: 'auto',
    fontFamily: 'Inter',
    fontSize: 10,
    fontWeight: 'normal',
    textAlign: 'center',
    padding: '20px 0',
    color: TEXT_SECONDARY,
  },
  // Section headers (Customer Service, Disclosures)
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 25,
    marginBottom: 10,
    borderBottom: `1.5px solid ${BRAND_COLOR}`,
    paddingBottom: 4,
  },
  sectionHeaderAccent: {
    width: 3,
    height: 14,
    backgroundColor: BRAND_COLOR,
    marginRight: 8,
  },
  sectionHeaderText: {
    fontFamily: 'Inter',
    fontSize: 10,
    fontWeight: 600,
    color: HEADER_BG,
    letterSpacing: 0.5,
  },
  // Customer service & disclosures body
  sectionBody: {
    fontFamily: 'Inter',
    fontSize: 9,
    fontWeight: 'normal',
    lineHeight: 1.6,
    textAlign: 'left',
    color: TEXT_DARK,
    marginBottom: 15,
  },
  bold: {
    fontWeight: 600,
  },
  space: {
    marginBottom: 8,
  },
  // Footer
  footerDivider: {
    position: 'absolute',
    borderBottomWidth: 0.5,
    borderBottomColor: BORDER_COLOR,
    bottom: 38,
    left: 35,
    right: 35,
  },
  footerText: {
    position: 'absolute',
    fontFamily: 'Inter',
    fontSize: 8,
    color: TEXT_SECONDARY,
    bottom: 22,
    left: 35,
  },
  pageNumber: {
    position: 'absolute',
    fontFamily: 'Inter',
    fontSize: 8,
    color: TEXT_SECONDARY,
    bottom: 22,
    right: 35,
  },
});

const IFILogo = ({ color = '#FFFFFF', size = 24 }: { color?: string; size?: number }) => {
  const aspectRatio = 100 / 72;
  const width = size * aspectRatio;
  return (
    <Svg width={width} height={size} viewBox="0 0 100 72">
      <G transform="translate(5, 3)">
        {/* I */}
        <Rect x="0" y="0" width="10" height="60" fill={color} />
        {/* F - top row */}
        <Rect x="25" y="0" width="10" height="10" fill={color} />
        <Rect x="37.5" y="0" width="10" height="10" fill={color} />
        <Rect x="50" y="0" width="10" height="10" fill={color} />
        {/* F - second row */}
        <Rect x="25" y="12.5" width="10" height="10" fill={color} />
        {/* F - middle row */}
        <Rect x="25" y="25" width="10" height="10" fill={color} />
        <Rect x="37.5" y="25" width="10" height="10" fill={color} />
        <Rect x="50" y="25" width="10" height="10" fill={color} />
        {/* F - fourth row */}
        <Rect x="25" y="37.5" width="10" height="10" fill={color} />
        {/* F - bottom row */}
        <Rect x="25" y="50" width="10" height="10" fill={color} />
        {/* I */}
        <Rect x="75" y="0" width="10" height="60" fill={color} />
      </G>
    </Svg>
  );
};

const StatementPDF = (props: IStatementPDFProps) => {
  const { statements } = props;
  const customerServiceAddress = CUSTOMER_SERVICE_SUBTITLE2;
  const customerServiceErrors = CUSTOMER_SERVICE_SUBTITLE3_2;

  const getFormattedAPY = (apy: string): string => {
    return parseFloat(apy).toFixed(2)
  }

  const filteredStatements = statements.filter(
    (statement: IStatementPDF) =>
      statement.transactions.length > 0 ||
      (statement.begginingBalance !== "0" || statement.endingBalance !== "0")
  );

  const statementPeriod = getStatementPeriod(statements[0].startPeriodDate.toString());

  return (
    <Document title={statementPeriod + " Statement"}>
      <Page size="A4" style={styles.page}>
        {/* Brand Header */}
        <View style={styles.brandBar} fixed>
          <View style={styles.logoContainer}>
            <IFILogo />
          </View>
          <View>
            <Text style={styles.statementLabel}>Account Statement</Text>
            <Text style={styles.statementPeriodLabel}>{statementPeriod}</Text>
          </View>
        </View>

        {/* Account Name */}
        <View style={styles.accountNameBar}>
          <Text style={styles.accountName}>{statements[0].accountName}</Text>
          <Text style={styles.accountSubdomain}>
            {statements[0].subdomain}.ifinetwork.com
          </Text>
        </View>

        {/* Account Info Strip */}
        <View style={styles.accountInfoStrip}>
          <View style={styles.accountInfoItem}>
            <Text style={styles.accountInfoLabel}>Account Number</Text>
            <Text style={styles.accountInfoValue}>{statements[0].accountId}</Text>
          </View>
          <View style={styles.accountInfoItem}>
            <Text style={styles.accountInfoLabel}>Statement Period</Text>
            <Text style={styles.accountInfoValue}>{statementPeriod}</Text>
          </View>
          <View style={styles.accountInfoItem}>
            <Text style={styles.accountInfoLabel}>Advisor</Text>
            <Text style={styles.accountInfoValue}>{statements[0].advisorName}</Text>
          </View>
        </View>

        {/* Product Tables */}
        <View style={styles.content}>
          {filteredStatements.length > 0 ? (
            <View>
              {filteredStatements.map((row, index) => (
                <View style={styles.table} key={index} wrap={false}>
                  {/* Table Header */}
                  <View style={styles.tableHeaderRow}>
                    <View style={styles.tableCol1Header}>
                      <Text style={styles.tableCellHeader}>Product</Text>
                    </View>
                    <View style={styles.tableColHeader}>
                      <Text style={styles.tableCellHeader}>
                        Beginning{"\n"}Balance *
                      </Text>
                    </View>
                    <View style={styles.tableColHeader}>
                      <Text style={styles.tableCellHeader}>
                        Net{"\n"}Transactions
                      </Text>
                    </View>
                    <View style={styles.tableColHeader}>
                      <Text style={styles.tableCellHeader}>
                        Ending{"\n"}Balance
                      </Text>
                    </View>
                    <View style={styles.tableColHeader}>
                      <Text style={styles.tableCellHeader}>
                        Net Accrued{"\n"}Interest **
                      </Text>
                    </View>
                    <View style={styles.tableColHeader}>
                      <Text style={styles.tableCellHeader}>
                        APY{"\n"}this period
                      </Text>
                    </View>
                  </View>

                  {/* Product Data Row */}
                  <View style={styles.tableRow}>
                    <View style={styles.tableCol1}>
                      <Text style={styles.tableCell}>
                        {row.productName} - {row.bankName}
                      </Text>
                    </View>
                    <View style={styles.tableCol}>
                      <Text style={styles.tableCell}>
                        ${formatCurrency(row.begginingBalance)}
                      </Text>
                    </View>
                    <View style={styles.tableCol}>
                      <Text style={styles.tableCell}>
                        ${formatCurrency(row.netTransaction)}
                      </Text>
                    </View>
                    <View style={styles.tableCol}>
                      <Text style={styles.tableCellBold}>
                        ${formatCurrency(row.endingBalance)}
                      </Text>
                    </View>
                    <View style={styles.tableCol}>
                      <Text style={styles.tableCell}>
                        ${formatCurrency(row.netAccruedInterest)}
                      </Text>
                    </View>
                    <View style={styles.tableCol}>
                      <Text style={styles.tableCell}>
                        {row.apyPeriod === "0" || isNaN(Number(row.apyPeriod))
                          ? "- "
                          : getFormattedAPY(row.apyPeriod)}
                        %
                      </Text>
                    </View>
                  </View>

                  {/* Transactions Sub-table */}
                  {row.transactions.length > 0 && (
                    <View style={styles.subTableContainer}>
                      <View style={styles.subTableHeaderRow}>
                        <View style={styles.tableCol1SubHeader}>
                          <Text style={styles.subCellHeader}>Date</Text>
                        </View>
                        <View style={styles.tableCol2SubHeader}>
                          <Text style={styles.subCellHeader}>Description</Text>
                        </View>
                        <View style={styles.tableColSubHeader}>
                          <Text style={styles.subCellHeader}>Amount</Text>
                        </View>
                      </View>
                      {row.transactions.map((transaction, tIndex) => (
                        <View key={tIndex} style={styles.subTableRow}>
                          <View style={styles.tableColSub1}>
                            <Text style={styles.subCell}>
                              {getLocaleDate(transaction.postedDate)}
                            </Text>
                          </View>
                          <View style={styles.tableColSub2}>
                            <Text style={styles.subCell}>
                              {transaction.type.type}
                            </Text>
                          </View>
                          <View style={styles.tableColSub}>
                            <Text style={styles.subCell}>
                              ${formatCurrency(transaction.amount)}
                            </Text>
                          </View>
                        </View>
                      ))}
                    </View>
                  )}

                  {/* Allocation History Sub-table */}
                  {row.allocationHistory.length > 0 && (
                    <View style={styles.subTableContainer} wrap={false}>
                      <View style={styles.subTableHeaderRow}>
                        <View style={styles.tableCol1SubHeader}>
                          <Text style={styles.subCellHeader}>Bank Name</Text>
                        </View>
                        <View style={styles.tableCol2SubHeader}>
                          <Text style={styles.subCellHeader}>Federal ID</Text>
                        </View>
                        <View style={styles.tableColSubHeader}>
                          <Text style={styles.subCellHeader}>Amount</Text>
                        </View>
                      </View>
                      {row.allocationHistory.map((historyItem, hIndex) => (
                        <View key={hIndex} style={styles.subTableRow}>
                          <View style={styles.tableColSub1}>
                            <Text style={styles.subCell}>
                              {historyItem.bank.name}
                            </Text>
                          </View>
                          <View style={styles.tableColSub2}>
                            <Text style={styles.subCell}>
                              {historyItem.bank.fdic}
                            </Text>
                          </View>
                          <View style={styles.tableColSub}>
                            <Text style={styles.subCell}>
                              ${formatCurrency(historyItem.amount)}
                            </Text>
                          </View>
                        </View>
                      ))}
                    </View>
                  )}
                </View>
              ))}
            </View>
          ) : (
            <View style={styles.table}>
              <View style={styles.tableHeaderRow}>
                <View style={styles.tableCol1Header}>
                  <Text style={styles.tableCellHeader}>Product</Text>
                </View>
                <View style={styles.tableColHeader}>
                  <Text style={styles.tableCellHeader}>
                    Beginning{"\n"}Balance *
                  </Text>
                </View>
                <View style={styles.tableColHeader}>
                  <Text style={styles.tableCellHeader}>
                    Net{"\n"}Transactions
                  </Text>
                </View>
                <View style={styles.tableColHeader}>
                  <Text style={styles.tableCellHeader}>
                    Ending{"\n"}Balance
                  </Text>
                </View>
                <View style={styles.tableColHeader}>
                  <Text style={styles.tableCellHeader}>
                    Net Accrued{"\n"}Interest **
                  </Text>
                </View>
                <View style={styles.tableColHeader}>
                  <Text style={styles.tableCellHeader}>
                    APY{"\n"}this period
                  </Text>
                </View>
              </View>
              <View style={styles.emptyTable}>
                <Text>No rows to display</Text>
              </View>
            </View>
          )}

          {/* Customer Service Section */}
          <View style={styles.sectionBody} wrap={false}>
            <View style={styles.sectionHeader}>
              <View style={styles.sectionHeaderAccent} />
              <Text style={styles.sectionHeaderText}>CUSTOMER SERVICE</Text>
            </View>
            <Text style={styles.bold}>{CUSTOMER_SERVICE_TITLE1}</Text>
            <Text style={styles.space}>{CUSTOMER_SERVICE_SUBTITLE1}</Text>
            <Text style={styles.bold}>{CUSTOMER_SERVICE_TITLE2}</Text>
            {customerServiceAddress.map((paragraph, index) => (
              <Text key={index}>{paragraph}</Text>
            ))}
            <Text style={styles.space} />
            <Text style={styles.bold}>{CUSTOMER_SERVICE_TITLE3}</Text>
            <Text style={styles.space}>{CUSTOMER_SERVICE_SUBTITLE3_1}</Text>
            <Text style={styles.space} break />
            {customerServiceErrors.map((paragraph, index) => (
              <Text key={index}>{paragraph}</Text>
            ))}
            <Text style={styles.space} />
            <Text style={styles.space}>{CUSTOMER_SERVICE_SUBTITLE3_3}</Text>
          </View>

          {/* Disclosures Section */}
          <View style={styles.sectionBody} wrap={false}>
            <View style={styles.sectionHeader}>
              <View style={styles.sectionHeaderAccent} />
              <Text style={styles.sectionHeaderText}>DISCLOSURES</Text>
            </View>
            <Text style={styles.space}>
              *<Text style={styles.bold}>{DISCLOSURES_TITLE1}</Text>
              {DISCLOSURES_PARAGRAPH1}
            </Text>
            <Text style={styles.space}>
              **
              <Text style={styles.bold}>{DISCLOSURES_TITLE2}</Text>
              {DISCLOSURES_PARAGRAPH2}
            </Text>
          </View>
        </View>

        {/* Footer */}
        <Text style={styles.footerDivider} fixed />
        <Text style={styles.footerText} fixed>
          powered by ifinetwork.com
        </Text>
        <Text
          style={styles.pageNumber}
          render={({ pageNumber, totalPages }) =>
            `Page ${pageNumber} of ${totalPages}`
          }
          fixed
        />
      </Page>
    </Document>
  );
};

export default StatementPDF;
